import React, { useState, useEffect } from 'react'
import type { AgentProvenanceEntry } from '../../state_manager/data_models/agent_provenance_entry.ts'
import { useSelector } from 'react-redux'
import { Navbar } from './parts/Navbar.tsx'
import { LeftMenu } from './parts/LeftMenu.tsx'
import { RightMenu } from './parts/RightMenu.tsx'
import { TabNavigation } from './parts/TabNavigation.tsx'
import { GlobalQuickAddDrawer } from './parts/GlobalQuickAddDrawer.tsx'
import { QuickAddProvider } from '../contexts/QuickAddContext.jsx'

import type { ReactNode } from 'react'
import type { RootState } from '../../state_manager/store.ts'
import type { FooterSelectItem, MenuSection } from '../../ui_configs/left_menu.ts'

export interface WrapperProps {
  children?: ReactNode
  menuSections?: MenuSection[]
  footerSelectItems?: FooterSelectItem[]
  currentPath?: string
  footerSelectionKey?: string | null
  selectedMenuKeys?: string[] | null
  rightMenuContent?: ReactNode
  showLeftMenu?: boolean
  showRightMenu?: boolean
  showTabNavigation?: boolean
  drawerContent?: ReactNode
  drawerTitle?: string
  drawerActions?: ReactNode
  onDrawerCancel?: (() => void | Promise<void>) | null
  onDrawerSubmit?: (() => void | Promise<void>) | null
  onDrawerRefresh?: (() => void | Promise<void>) | null
  drawerLoading?: boolean
  viewFullRecord?: boolean
  /** Right menu width as a CSS length, e.g. '35%' */
  rightMenuWidth?: string
  showDrawerActions?: boolean
  /** Record data passed to RightMenu for View Full Record */
  selectedRecord?: Record<string, unknown> | null
  /** Soft delete from the drawer: confirm text + the delete action */
  drawerDeleteConfirm?: { title: string; description?: string; onConfirm: () => Promise<void> } | null
  /** Record provenance for the drawer banner — see RightMenu's `provenance`. */
  provenance?: {
    createdByName?: string | null
    dateCreated?: string | null
    agentProvenance?: AgentProvenanceEntry | null
  } | null
}

export function Wrapper({
  children,
  menuSections = [],
  footerSelectItems = [],
  currentPath = '',
  footerSelectionKey = null,
  selectedMenuKeys = null,
  rightMenuContent,
  showLeftMenu = true,
  showRightMenu = true,
  showTabNavigation = true,
  // New drawer-specific props
  drawerContent = null,
  drawerTitle = '',
  drawerActions = null,
  onDrawerCancel = null,
  onDrawerSubmit = null,
  onDrawerRefresh = null,
  drawerLoading = false,
  viewFullRecord = false,
  rightMenuWidth = '35%', // New prop for dynamic width
  showDrawerActions = true, // New prop to control drawer actions
  selectedRecord = null, // Record data passed to RightMenu for View Full Record
  drawerDeleteConfirm = null,
  provenance = null
}: WrapperProps) {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth)

  // Initialize state from localStorage to persist across navigation
  const [isLeftMenuCollapsed, setIsLeftMenuCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebar-collapsed')
      return saved === 'true'
    }
    return false
  })

  // Save state to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sidebar-collapsed', String(isLeftMenuCollapsed))
    }
  }, [isLeftMenuCollapsed])

  const toggleLeftMenu = () => {
    // Only toggle sidebar when burger menu is clicked
    // Menu item clicks should NOT affect the collapse state
    setIsLeftMenuCollapsed(!isLeftMenuCollapsed)
  }

  return (
    // h-dvh / min-h-dvh (not h-screen) so the layout tracks iOS Safari's
    // dynamic viewport correctly instead of being clipped by the toolbar.
    // QuickAddProvider links the navbar's + menu to the global quick-add
    // overlay (create forms open alone, no module grid).
    <QuickAddProvider>
      <div className='min-h-dvh bg-gray-50 text-gray-900'>
        {/* Navbar - Always visible */}
        <div className='fixed top-0 left-0 right-0 z-50'>
          <Navbar onToggleSidebar={toggleLeftMenu} />
        </div>

        {/* Main content area */}
        <div className='pt-10 h-dvh flex'>
          {/* Left Menu - Only visible when authenticated */}
          <LeftMenu
            sections={menuSections}
            footerSelectItems={footerSelectItems}
            currentPath={currentPath}
            footerSelectionKey={footerSelectionKey}
            selectedMenuKeys={selectedMenuKeys}
            isVisible={isAuthenticated && showLeftMenu}
            isCollapsed={isLeftMenuCollapsed}
          />

          {/* Main content - Adjusts based on sidebar state */}
          <div className='flex-1 overflow-y-auto transition-all duration-300 flex flex-col'>
            {/* Tab Navigation - Only visible when authenticated and enabled */}
            {isAuthenticated && showTabNavigation ? (
              <TabNavigation />
            ) : (
              <main className='flex-1 min-h-full'>{children}</main>
            )}
          </div>

          {/* Right Menu - Only visible when authenticated */}
          <RightMenu
            isVisible={isAuthenticated && showRightMenu}
            drawerContent={drawerContent}
            drawerTitle={drawerTitle}
            drawerActions={drawerActions}
            onCancel={onDrawerCancel}
            onSubmit={onDrawerSubmit}
            onRefresh={onDrawerRefresh}
            loading={drawerLoading}
            viewFullRecord={viewFullRecord}
            width={rightMenuWidth}
            showActions={showDrawerActions}
            selectedRecord={selectedRecord}
            drawerDeleteConfirm={drawerDeleteConfirm}
            provenance={provenance}
          >
            {rightMenuContent}
          </RightMenu>
        </div>

        {/* Global quick-add overlay — renders only while an entity is open */}
        <GlobalQuickAddDrawer />
      </div>
    </QuickAddProvider>
  )
}
